import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLazyQuery } from '@apollo/client';
import { isEmpty, map } from 'lodash';
import Typography from '@material-ui/core/Typography';
import Config from 'config';
import KEY_AVERAGE_WEEKLY_TIME_SPENT_ASYNC_QUERY from 'graph/reports/key-average-weekly-total-time-spent-report-async-query';
import BarChartCard, { ChartData } from '../../../../charts/BarChartCard';
import useReportQueryParams from '../../../../hooks/use-report-query-params';
import { getQueryVariables } from '../../../../utils/report-param-utils';

const AverageWeeklyTimeSpentChart: React.FC = () => {
  const { t } = useTranslation(['Reports', 'ReportsHelpCommon']);
  const [{ entityId, entityType, dateRange }] = useReportQueryParams();
  const [fetchReport, { data, error, stopPolling }] = useLazyQuery(
    KEY_AVERAGE_WEEKLY_TIME_SPENT_ASYNC_QUERY,
    {
      pollInterval: Config.reporting.asyncPollIntervalMs
    }
  );

  useEffect(() => {
    fetchReport({ variables: getQueryVariables({ entityId, entityType, dateRange }) as any });
  }, [fetchReport, entityId, entityType, dateRange]);

  const chartData: ChartData = {};

  if (error) {
    stopPolling?.();
  } else if (data?.keyAverageWeeklyTimeSpentReportAsync?.result) {
    stopPolling?.();

    const {
      curriculumAverageWeeklyTimeSpent = [],
      averageWeeklyMinutesSpent
    } = data.keyAverageWeeklyTimeSpentReportAsync.result;

    chartData.series = map(curriculumAverageWeeklyTimeSpent, 'averageWeeklyMinutesSpent');
    chartData.categories = map(curriculumAverageWeeklyTimeSpent, 'productCurriculumCode');

    const ChartInformation = (): React.ReactNode => (
      <Typography variant="h2" component="span">
        {t(`{{averageWeeklyMinutes}} minutes`, {
          averageWeeklyMinutes: averageWeeklyMinutesSpent
        })}
      </Typography>
    );

    chartData.renderTextInformation = ChartInformation;
  }

  return (
    <BarChartCard
      title={t('Average Weekly Time Spent')}
      yaxisTitle={t('Minutes')}
      helpText={t('averageWeeklyTimeSpent', { ns: 'ReportsHelpCommon' })}
      loading={isEmpty(chartData)}
      {...chartData}
    />
  );
};

export default React.memo(AverageWeeklyTimeSpentChart);
